/**
 * Calendar availability types for booking flow
 * Based on /api/calendar-availability-new endpoint
 */

import type { ServiceType } from '../state/types';

/**
 * Calendar day type constant for early access / request days.
 * Used to detect when a date triggers the early access flow.
 */
export const CALENDAR_DAY_TYPE_REQUEST: CalendarDayType = 'request';

/**
 * Calendar day availability type
 * Determines how the day is displayed in the calendar
 */
export type CalendarDayType =
  | 'available'              // Day with normal availability
  | 'request'                // Day with request/early access
  | 'waiting-list'           // Only waiting list available
  | 'walk-in'                // Only walk-in available
  | 'not-available'          // No availability (but not closed)
  | 'not-available-closed'   // Restaurant is closed
  | 'not-available-old-day'; // Past day

/**
 * Single day in the calendar availability response
 */
export interface CalendarDayInfo {
  /** ISO date string (e.g., "2025-10-04T00:00:00+00:00") */
  date: string;
  /** Availability flag as string ("0" or "1") */
  isAvailable: '0' | '1';
  /** Preferred availability flag as string */
  isAvailablePreferred: '0' | '1';
  /** Whether waiting list is full */
  waitingListFull: '0' | '1';
  /** Whether this is a special day (event, holiday, etc.) */
  isSpecialDay: boolean;
  /** Whether to highlight the special day in UI */
  highlightSpecialDay: boolean;
  /** Type of availability for this day */
  type: CalendarDayType;
  /** Center hour for display purposes */
  centerHour: string;
  /** Minimum order amount for delivery/takeaway */
  minimumOrderAmount: number | null;
}

/**
 * Response from /api/calendar-availability-new endpoint
 */
export interface CalendarAvailabilityResponse {
  /** Array of 59 days with availability info */
  calendarInfo: CalendarDayInfo[];
}

/**
 * Calendar availability with service type attached
 */
export interface ServiceCalendarAvailability extends CalendarAvailabilityResponse {
  /** The service type this calendar belongs to */
  serviceType: ServiceType;
}

/**
 * Display type for visual styling (simplified from CalendarDayType)
 */
export type AvailabilityDisplayType =
  | 'available'      // Green, clickable
  | 'request'        // Green, requires approval
  | 'waiting-list'   // Yellow/orange, limited
  | 'walk-in'        // Blue, walk-in only
  | 'not-available'  // Gray, disabled
  | 'closed';        // Dark gray with strikethrough, disabled

/**
 * Merged calendar availability for all services
 * Key is the date (YYYY-MM-DD), value contains availability per service
 */
export interface MergedCalendarDay {
  /** Date in YYYY-MM-DD format */
  date: string;
  /** Raw service data */
  services: {
    [K in ServiceType]?: CalendarDayInfo;
  };
  /** Whether any service has availability */
  hasAnyAvailability: boolean;
  /** Whether any service has preferred-only availability */
  hasPreferredAvailability: boolean;
  /** Whether waiting list is full across all services with availability (AND logic) */
  isWaitingListFull: boolean;
  /** List of service types that are available (not closed) */
  availableServiceTypes: ServiceType[];
  /** Merged type for display (best available across all services) */
  type: CalendarDayType;
  /** Display type for visual styling */
  displayType: AvailabilityDisplayType;
  /** Whether this is a special day (event, holiday, etc.) */
  isSpecialDay: boolean;
  /** Whether to highlight the special day with star icon */
  highlightSpecialDay: boolean;
  /** Center hour for display */
  centerHour: string;
  /** Minimum order amount (for delivery/takeaway) */
  minimumOrderAmount: number | null;
}

/**
 * Result of the useCalendarAvailability hook
 */
export interface CalendarAvailabilityResult {
  /** Raw calendar data per service */
  calendarByService: ServiceCalendarAvailability[];
  /** Merged calendar with all services */
  mergedCalendar: MergedCalendarDay[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
}

/**
 * Parameters for calendar availability query
 */
export interface CalendarAvailabilityParams {
  restaurantId: number;
  partySize: number;
  serviceTypes: ServiceType[];
}
